import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import { createWorker } from "tesseract.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
const MIN_NATIVE_CHARACTERS = 24;

function positioned(item) {
  const transform = item.transform || [];
  return { text: item.str || "", x: transform[4] || 0, y: transform[5] || 0, width: item.width || 0, height: item.height || 0 };
}

function textLines(items) {
  const lines = new Map();
  items.forEach((item) => { const key = Math.round(item.y / 3) * 3; lines.set(key, [...(lines.get(key) || []), item]); });
  return [...lines.entries()].sort((a, b) => b[0] - a[0]).map(([, line]) => line.sort((a, b) => a.x - b.x).map((item) => item.text).join(" ").replace(/\s+/g, " ").trim()).filter(Boolean).join("\n");
}

function ocrItems(data) {
  return (data.words || []).map((word) => ({
    text: word.text || "",
    x: word.bbox?.x0 || 0,
    y: word.bbox?.y0 || 0,
    width: Math.max(0, (word.bbox?.x1 || 0) - (word.bbox?.x0 || 0)),
    height: Math.max(0, (word.bbox?.y1 || 0) - (word.bbox?.y0 || 0)),
    confidence: word.confidence ?? null,
  })).filter((word) => word.text.trim());
}

async function recognize(page, worker, onProgress) {
  const viewport = page.getViewport({ scale: 3 });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
  await page.render({ canvasContext: canvas.getContext("2d", { willReadFrequently: true }), viewport }).promise;
  const { data } = await worker.recognize(canvas, {}, { text: true, blocks: true });
  onProgress?.(1);
  return { text: data.text || "", confidence: data.confidence ?? null, items: ocrItems(data) };
}

export async function extractPdf(file, onProgress) {
  const task = pdfjsLib.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const pdf = await task.promise; const pages = []; let ocrWorker = null; let ocrPageNumber = 1;
  try { for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    onProgress?.({ pageNumber, pageCount: pdf.numPages, phase: "native", progress: (pageNumber - 1) / pdf.numPages });
    const page = await pdf.getPage(pageNumber); const content = await page.getTextContent(); const items = content.items.map(positioned);
    let text = textLines(items); let extractionMethod = "native"; let confidence = null;
    if (text.replace(/\s/g, "").length < MIN_NATIVE_CHARACTERS) {
      extractionMethod = "ocr";
      ocrPageNumber = pageNumber;
      if (!ocrWorker) ocrWorker = await createWorker("eng", 1, { logger: (message) => message.status === "recognizing text" && onProgress?.({ pageNumber: ocrPageNumber, pageCount: pdf.numPages, phase: "ocr", progress: ((ocrPageNumber - 1) + message.progress) / pdf.numPages }) });
      const result = await recognize(page, ocrWorker, (value) => onProgress?.({ pageNumber, pageCount: pdf.numPages, phase: "ocr", progress: ((pageNumber - 1) + value) / pdf.numPages }));
      text = result.text; confidence = result.confidence; items.splice(0, items.length, ...result.items);
    }
    pages.push({ pageNumber, extractionMethod, text, items, confidence }); page.cleanup();
  }} finally { if (ocrWorker) await ocrWorker.terminate(); await task.destroy(); }
  onProgress?.({ pageNumber: pdf.numPages, pageCount: pdf.numPages, phase: "complete", progress: 1 }); return { pages };
}

export async function sha256Hex(file) {
  if (!globalThis.crypto?.subtle) return null;
  const digest = await globalThis.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}
